import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { fetchSongV2ById, updateSongV2, UpdateSongV2Data, SongV2 } from "./songV2Api";
import styles from "./SongV2Shared.module.css";
import {
  Music,
  ArrowLeft,
  Save,
  AlertCircle,
  Clock,
  Volume2,
  Loader,
  Play,
  Pause,
  RotateCcw,
} from "lucide-react";
import FallingNotesStage from "../songsterr/components/FallingNotesStage";
import TimelineEditor from "../songsterr/components/TimelineEditor";
import { buildTempoIndex, maskAtMs } from "../songsterr/tempoUtils";
import {
  parseYoutubeVideoId,
  ensureYoutubeIframeApi,
  setPlaybackRateSafe,
} from "../songsterr/youtubeUtils";
import type { ScoreV2 } from "../songsterr/types";

export default function EditSongV2() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Full song data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [songData, setSongData] = useState<SongV2 | null>(null);
  
  // Form fields
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [bpm, setBpm] = useState(120);
  const [ts, setTs] = useState("4/4");
  const [durationMs, setDurationMs] = useState(180000);
  const [syncMs, setSyncMs] = useState(0);
  const [lookaheadMs, setLookaheadMs] = useState(2200);
  const [hitMs, setHitMs] = useState(80);
  const [calibrateSpeed, setCalibrateSpeed] = useState(1.0);
  const [isLocked, setIsLocked] = useState(false);
  
  // dt/m arrays for timeline editing
  const [dtArray, setDtArray] = useState<number[]>([]);
  const [mArray, setMArray] = useState<number[]>([]);
  
  // Timeline Editor edited state
  const [editedEvents, setEditedEvents] = useState<Array<{ ms: number; m: number }> | null>(null);
  const hasUnsavedTimelineChanges = editedEvents !== null;
  
  // YouTube player
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const videoId = useMemo(() => parseYoutubeVideoId(youtubeUrl.trim()).videoId, [youtubeUrl]);
  const ytHostRef = useRef<HTMLDivElement | null>(null);
  const ytPlayerRef = useRef<any>(null);
  const [ytReady, setYtReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number>(0);
  
  // Position tracking
  const [posMs, setPosMs] = useState(0);
  const [ytMsNow, setYtMsNow] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState("");

  // Build score from dt/m arrays (use editedEvents if available for live preview)
  const scoreFromDtM: ScoreV2 | null = useMemo(() => {
    // If there are edited events, use them for live preview
    const sourceDt = editedEvents ? editedEvents.map(e => e.ms) : dtArray;
    const sourceM = editedEvents ? editedEvents.map(e => e.m) : mArray;
    
    if (!sourceDt.length || !sourceM.length) return null;
    
    const events: Array<{ t0: number; dt: number; m: number }> = [];
    let absMs = 0;
    for (let i = 0; i < sourceDt.length; i++) {
      if (i === 0) {
        absMs = sourceDt[0];
      } else {
        absMs += sourceDt[i];
      }
      events.push({
        t0: absMs,
        dt: sourceDt[i],
        m: sourceM[i] || 0,
      });
    }
    
    return {
      ppq: 1,
      tempo_map: [{ tick: 0, bpm: 60000 }],
      events,
    };
  }, [dtArray, mArray, editedEvents]);

  const tempoIndex = useMemo(
    () => (scoreFromDtM ? buildTempoIndex(scoreFromDtM.tempo_map ?? [], scoreFromDtM.ppq) : []),
    [scoreFromDtM]
  );

  const activeMask = useMemo(() => {
    if (!scoreFromDtM) return 0;
    return maskAtMs(scoreFromDtM, tempoIndex, posMs);
  }, [scoreFromDtM, tempoIndex, posMs]);

  useEffect(() => {
    if (id) loadSong(id);
  }, [id]);

  const loadSong = async (songId: string) => {
    try {
      setLoading(true);
      const data = await fetchSongV2ById(songId);
      setSongData(data);
      setTitle(data.title || "");
      setArtist(data.artist || "");
      setBpm(data.bpm || 120);
      setTs(data.ts || "4/4");
      setDurationMs(data.duration_ms || 180000);
      setSyncMs(data.sync_ms || 0);
      setLookaheadMs(data.lookahead_ms || 2200);
      setHitMs(data.hit_ms || 80);
      setCalibrateSpeed(data.calibrate_speed || 1.0);
      setIsLocked(data.is_locked || false);
      setYoutubeUrl(data.source?.url || "");
      setDtArray(data.dt || []);
      setMArray(data.m || []);
    } catch (error: any) {
      console.error("Failed to load song:", error);
      setErrors({
        general: error.response?.data?.message || "Şarkı bilgisi yüklenemedi.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize YouTube player
  useEffect(() => {
    if (!videoId || loading) return;
    
    let cancelled = false;

    (async () => {
      await ensureYoutubeIframeApi();
      if (cancelled) return;
      if (!ytHostRef.current) return;

      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);

      ytPlayerRef.current = new window.YT.Player(ytHostRef.current, {
        width: "100%",
        height: "280",
        videoId,
        playerVars: { playsinline: 1, controls: 1, rel: 0 },
        events: {
          onReady: () => {
            setYtReady(true);
            setPlaybackRateSafe(ytPlayerRef.current, speed);
          },
          onStateChange: (e: any) => {
            const st = e?.data;
            if (st === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
            } else {
              setIsPlaying(false);
            }
          },
        },
      });
    })();

    return () => {
      cancelled = true;
      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
      setYtReady(false);
      setIsPlaying(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, loading]);

  // Apply speed
  useEffect(() => {
    const p = ytPlayerRef.current;
    if (!p || !ytReady) return;
    setPlaybackRateSafe(p, speed);
  }, [speed, ytReady]);

  // RAF: posMs = ytMs - syncMs
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const loop = () => {
      const p = ytPlayerRef.current;
      if (p && p.getCurrentTime) {
        const ytMs = Number(p.getCurrentTime() || 0) * 1000;
        setYtMsNow(ytMs);
        setPosMs(ytMs - syncMs);
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, syncMs]);

  const togglePlay = () => {
    const p = ytPlayerRef.current;
    if (!p || !ytReady) return;

    if (isPlaying) {
      p.pauseVideo?.();
    } else {
      p.playVideo?.();
    }
  };

  const restartFromBeginning = () => {
    const p = ytPlayerRef.current;
    if (!p || !p.seekTo) return;

    const ytMsTarget = Math.max(0, syncMs);
    p.seekTo(ytMsTarget / 1000, true);
    setPosMs(0);
    setYtMsNow(ytMsTarget);
  };

  const handleSyncMsChange = useCallback((newSyncMs: number) => {
    setSyncMs(newSyncMs);
  }, []);

  const applyTimelineChanges = () => {
    if (!editedEvents) return;
    const newDt = editedEvents.map(e => e.ms);
    const newM = editedEvents.map(e => e.m);
    setDtArray(newDt);
    setMArray(newM);
    setEditedEvents(null);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = "Başlık zorunludur";
    if (!artist.trim()) newErrors.artist = "Sanatçı zorunludur";
    if (bpm < 20 || bpm > 300) newErrors.bpm = "BPM 20-300 arasında olmalıdır";
    if (durationMs < 1000) newErrors.durationMs = "Süre en az 1 saniye olmalıdır";
    if (calibrateSpeed < 0.25 || calibrateSpeed > 2.0)
      newErrors.calibrateSpeed = "Calibrate speed 0.25-2.0 arasında olmalıdır";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage("");

    try {
      const data: UpdateSongV2Data = {
        title: title.trim(),
        artist: artist.trim(),
        bpm,
        ts: ts.trim(),
        duration_ms: durationMs,
        sync_ms: syncMs,
        lookahead_ms: lookaheadMs,
        hit_ms: hitMs,
        calibrate_speed: calibrateSpeed,
        is_locked: isLocked,
        dt: dtArray,
        m: mArray,
      };

      await updateSongV2(id, data);
      setSuccessMessage("✅ Şarkı başarıyla güncellendi!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Update failed:", error);
      
      if (error.response?.status === 401) {
        setErrors({
          general: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
        });
      } else if (error.response?.status === 403) {
        setErrors({
          general: "Bu işlem için admin yetkisi gerekiyor.",
        });
      } else if (error.response?.status === 400) {
        setErrors({
          general: "Gönderilen veriler hatalı. Lütfen kontrol edin.",
        });
      } else {
        setErrors({
          general: "Güncelleme sırasında hata oluştu. Lütfen tekrar deneyin.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loader className={styles.loadingSpinner} size={48} />
          <span>Şarkı yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <Music className={styles.titleIcon} />
            Şarkıyı Düzenle (V2)
          </h1>
          <p className={styles.subtitle}>{title} - {artist}</p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/songsv2" className={styles.secondaryButton}>
            <ArrowLeft size={16} />
            Geri Dön
          </Link>
        </div>
      </div>

      {errors.general && (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{errors.general}</span>
        </div>
      )}

      {successMessage && (
        <div style={{
          background: "#10b981",
          color: "white",
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 16,
          fontWeight: 600,
        }}>
          {successMessage}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
        {/* LEFT COLUMN - Form */}
        <div>
          <form onSubmit={handleSubmit}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Music size={20} />
                <h2>Temel Bilgiler</h2>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.grid2}>
                  <div>
                    <label className={styles.label}>
                      Başlık <span className={styles.required}>*</span>
                    </label>
                    <input
                      className={styles.input}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Şarkı başlığı"
                    />
                    {errors.title && <span className={styles.error}>{errors.title}</span>}
                  </div>
                  <div>
                    <label className={styles.label}>
                      Sanatçı <span className={styles.required}>*</span>
                    </label>
                    <input
                      className={styles.input}
                      value={artist}
                      onChange={(e) => setArtist(e.target.value)}
                      placeholder="Sanatçı adı"
                    />
                    {errors.artist && <span className={styles.error}>{errors.artist}</span>}
                  </div>
                </div>

                <div className={styles.grid3}>
                  <div>
                    <label className={styles.label}>BPM</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={bpm}
                      onChange={(e) => setBpm(parseInt(e.target.value) || 120)}
                      min={20}
                      max={300}
                    />
                    {errors.bpm && <span className={styles.error}>{errors.bpm}</span>}
                  </div>
                  <div>
                    <label className={styles.label}>Time Signature</label>
                    <input
                      className={styles.input}
                      value={ts}
                      onChange={(e) => setTs(e.target.value)}
                      placeholder="4/4"
                    />
                  </div>
                  <div>
                    <label className={styles.label}>Süre (ms)</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={durationMs}
                      onChange={(e) => setDurationMs(parseInt(e.target.value) || 180000)}
                      min={1000}
                    />
                    {errors.durationMs && <span className={styles.error}>{errors.durationMs}</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Clock size={20} />
                <h2>Zamanlama Ayarları</h2>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.grid2}>
                  <div>
                    <label className={styles.label}>Sync (ms)</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={syncMs}
                      onChange={(e) => setSyncMs(parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <label className={styles.label}>Lookahead (ms)</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={lookaheadMs}
                      onChange={(e) => setLookaheadMs(parseInt(e.target.value) || 2200)}
                    />
                  </div>
                  <div>
                    <label className={styles.label}>Hit Window (ms)</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={hitMs}
                      onChange={(e) => setHitMs(parseInt(e.target.value) || 80)}
                    />
                  </div>
                  <div>
                    <label className={styles.label}>Calibrate Speed</label>
                    <input
                      type="number"
                      step="0.1"
                      className={styles.input}
                      value={calibrateSpeed}
                      onChange={(e) => setCalibrateSpeed(parseFloat(e.target.value) || 1.0)}
                      min={0.25}
                      max={2.0}
                    />
                    {errors.calibrateSpeed && (
                      <span className={styles.error}>{errors.calibrateSpeed}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Volume2 size={20} />
                <h2>Diğer Ayarlar</h2>
              </div>
              <div className={styles.cardBody}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isLocked}
                    onChange={(e) => setIsLocked(e.target.checked)}
                  />
                  <span>Kilitli (Kullanıcılar değiştiremez)</span>
                </label>
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={() => navigate("/songsv2")}
                className={styles.secondaryButton}
                disabled={isSubmitting}
              >
                İptal
              </button>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className={styles.spinner}></div>
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Güncelle
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* MIDDLE COLUMN - YouTube Player */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* YouTube Player */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Play size={20} />
              <h2>YouTube Player</h2>
            </div>
            <div className={styles.cardBody}>
              {videoId ? (
                <>
                  <div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
                    <div ref={ytHostRef} />
                  </div>
                  
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={togglePlay}
                      disabled={!ytReady}
                      style={{
                        borderRadius: 8,
                        background: isPlaying ? "#ef4444" : "#10b981",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        cursor: ytReady ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontWeight: 600,
                      }}
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                      {isPlaying ? "Pause" : "Play"}
                    </button>
                    
                    <button
                      type="button"
                      onClick={restartFromBeginning}
                      disabled={!ytReady}
                      style={{
                        borderRadius: 8,
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        cursor: ytReady ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontWeight: 600,
                      }}
                    >
                      <RotateCcw size={16} />
                      Başa Dön
                    </button>

                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                      <label style={{ fontSize: 12, color: "#64748b" }}>Speed:</label>
                      <select
                        value={speed}
                        onChange={(e) => setSpeed(Number(e.target.value))}
                        style={{
                          borderRadius: 6,
                          padding: "4px 8px",
                          background: "#1e293b",
                          color: "#f1f5f9",
                          border: "1px solid #334155",
                        }}
                      >
                        <option value={0.25}>0.25x</option>
                        <option value={0.5}>0.5x</option>
                        <option value={0.75}>0.75x</option>
                        <option value={1.0}>1.0x</option>
                        <option value={1.25}>1.25x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2.0}>2.0x</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", fontFamily: "monospace" }}>
                    ytMs: {Math.round(ytMsNow)} · tabMs: {Math.round(posMs)} · syncMs: {syncMs}
                  </div>
                </>
              ) : (
                <div style={{ color: "#64748b", textAlign: "center", padding: 20 }}>
                  YouTube URL bulunamadı
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Quick Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Quick Info */}
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                <div><span role="img" aria-label="notepad">📝</span> Toplam Nota: {dtArray.length}</div>
                <div><span role="img" aria-label="music">🎵</span> Video ID: {videoId || "—"}</div>
                <div><span role="img" aria-label="timer">⏱️</span> Sync Offset: {syncMs}ms</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FULL WIDTH: Note Rain */}
      {scoreFromDtM && (
        <div 
          className={styles.card} 
          style={{ marginTop: 20 }}
        >
          <div className={styles.cardHeader}>
            <Music size={20} />
            <h2>Note Rain</h2>
          </div>
          <div className={styles.cardBody}>
            <FallingNotesStage
              score={scoreFromDtM}
              tempoIndex={tempoIndex}
              posMs={posMs}
              activeMask={activeMask}
              lookaheadMs={lookaheadMs}
              hitWindowMs={Math.max(10, Math.min(180, hitMs * 0.35))}
              speed={speed}
            />
          </div>
        </div>
      )}

      {/* FULL WIDTH: Timeline Editor */}
      {dtArray.length > 0 && mArray.length > 0 && (
        <div 
          className={styles.card}
          style={{
            marginTop: 20,
            border: hasUnsavedTimelineChanges ? "2px solid #f59e0b" : undefined,
          }}
        >
          <div className={styles.cardHeader}>
            <Clock size={20} />
            <h2><span role="img" aria-label="chart">📊</span> Timeline Editor</h2>
          </div>
          <div className={styles.cardBody}>
            {hasUnsavedTimelineChanges && (
              <div
                style={{
                  background: "#78350f",
                  border: "1px solid #f59e0b",
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ color: "#fbbf24", fontSize: 13, fontWeight: 600 }}>
                  <span role="img" aria-label="warning">⚠️</span> Kaydedilmemiş değişiklikler var
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setEditedEvents(null)}
                    style={{
                      borderRadius: 6,
                      background: "#374151",
                      border: "none",
                      color: "#f1f5f9",
                      padding: "6px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    <span role="img" aria-label="cancel">❌</span> İptal
                  </button>
                  <button
                    type="button"
                    onClick={applyTimelineChanges}
                    style={{
                      borderRadius: 6,
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      border: "none",
                      color: "white",
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <span role="img" aria-label="check">✅</span> Değişiklikleri Uygula
                  </button>
                </div>
              </div>
            )}
            
            <TimelineEditor
              events={editedEvents || dtArray.map((dt, i) => ({
                ms: dt,
                m: mArray[i] || 0,
              }))}
              syncMs={syncMs}
              onEventsChange={(newEvents) => {
                setEditedEvents(newEvents);
              }}
              onSyncMsChange={handleSyncMsChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
